// API de poses personalizadas (Interfaz V2). Tabla `poses_personalizadas`
// (RLS por empresa); imágenes en el bucket privado `modelos`, path
// `<id_company>/poses_custom/<filename>` → signed URLs para mostrar. El
// id_company va PRIMERO porque la policy del bucket `modelos` exige que el
// path empiece con `<id_company>/`.
//
// Crear: NO se guarda desde el front — el flujo n8n `procesar_pose_subida`
// genera el muñeco-en-pose, guarda (service_role) y devuelve la pose. Acá solo
// disparamos el webhook; el caller refetchea. Borrar: el cliente borra las
// suyas (política RLS DELETE acotada) + remueve la imagen del storage.

import { supabase } from '../../lib/supabase';
import { PROCESAR_POSE_URL, GENERATE_AUTH_TOKEN } from '../../config';
import { normalizeImageForUpload, UnsupportedImageFormatError } from '../../utils/heicConvert';
import type { PoseV2 } from './posesApi';

const BUCKET = 'modelos';
const SIGNED_TTL = 3600;

// File → base64 sin el prefix `data:...,` (mismo helper que modelsApi).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

// El backend puede devolver el b64 con o sin prefix `data:image/...;base64,`.
function stripB64Prefix(raw: string): string {
  const idx = raw.indexOf(',');
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

// base64 → Blob para subir al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

/** Trae las poses personalizadas de la empresa, con signed URL para mostrar. */
export async function fetchMyCustomPoses(idCompany: string): Promise<PoseV2[]> {
  const { data, error } = await supabase
    .from('poses_personalizadas')
    .select('id, nombre, filename, cantidad_personas')
    .eq('id_company', idCompany)
    .order('created_at', { ascending: false });
  if (error || !data) {
    if (error) console.error('[posesCustomApi] fetchMyCustomPoses error', error);
    return [];
  }
  const out: PoseV2[] = [];
  for (const p of data as Array<{
    id: string;
    nombre: string | null;
    filename: string;
    cantidad_personas: number | null;
  }>) {
    const path = `${idCompany}/poses_custom/${p.filename}`;
    const { data: signed } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, SIGNED_TTL);
    out.push({
      id: String(p.id),
      nombre: p.nombre || 'Mi pose',
      filename: p.filename,
      categoria: null,
      imageUrl: signed?.signedUrl || '',
      areasPrincipales: [],
      cantidadPersonas: p.cantidad_personas ?? 1,
      isCustom: true,
    });
  }
  return out;
}

/**
 * Sube una pose en 2 pasos:
 *  1. Manda la foto al webhook `procesar_pose_subida`. n8n copia la pose sobre
 *     el muñeco, descuenta 2 imágenes y devuelve el b64 (`image_pose`).
 *  2. El front guarda esa imagen generada: la sube al bucket `modelos`
 *     (`<id_company>/poses_custom/<uuid>.png`) e inserta la fila en
 *     `poses_personalizadas`. La foto subida por el cliente se descarta.
 * El caller refetchea `fetchMyCustomPoses` al terminar.
 */
export async function uploadCustomPose(opts: {
  userId: string;
  idCompany: string;
  file: File;
  nombre?: string;
  /** Cantidad de personas de la pose. Sale del contexto (cantidad de
   *  modelos seleccionados). Default 1. El n8n genera ese número de muñecos. */
  cantidadPersonas?: number;
}): Promise<void> {
  // Normalizar formato igual que el resto de los uploads de V2: las fotos de
  // iPhone (.heic/.avif) viajaban crudas y el n8n / visor no las procesa.
  let file: File;
  try {
    file = await normalizeImageForUpload(opts.file);
  } catch (convErr) {
    // Un formato bloqueado (RAW/TIFF) ya trae su propio mensaje: re-lanzarlo tal
    // cual. El genérico queda solo para fallos reales de conversión (HEIC/AVIF).
    if (convErr instanceof UnsupportedImageFormatError) throw convErr;
    console.error('[posesCustomApi] conversión de formato falló', convErr);
    throw new Error(
      'No pudimos convertir la imagen (HEIC/AVIF). Probá con una imagen en formato png o jpg.',
    );
  }
  const imagen = await fileToBase64(file);
  // 1. Generar (n8n) ────────────────────────────────────────────────
  const res = await fetch(PROCESAR_POSE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({
      user_id: opts.userId,
      imagen,
      nombre: opts.nombre ?? null,
      cantidad_personas: opts.cantidadPersonas ?? 1,
    }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || `Error ${res.status} procesando la pose`);
  }
  const data = await res.json().catch(() => null);
  if (!data || data.response !== 'success' || typeof data.image_pose !== 'string') {
    throw new Error(
      (data && data.error_message) || 'Respuesta inesperada del servidor',
    );
  }
  // 2. Guardar (storage + fila) ─────────────────────────────────────
  const blob = b64ToBlob(stripB64Prefix(data.image_pose), 'image/png');
  const filename = `${crypto.randomUUID()}.png`;
  const path = `${opts.idCompany}/poses_custom/${filename}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/png', upsert: false });
  if (upErr) throw upErr;
  const { error: insErr } = await supabase
    .from('poses_personalizadas')
    .insert({
      id_company: opts.idCompany,
      nombre: opts.nombre ?? null,
      filename,
      cantidad_personas: opts.cantidadPersonas ?? 1,
    });
  if (insErr) throw insErr;
}

/** Borra una pose propia: remueve la imagen del storage + la fila (RLS DELETE
 *  acotada por empresa permite el borrado de la fila propia). */
export async function deleteCustomPose(opts: {
  id: string;
  idCompany: string;
  filename: string;
}): Promise<void> {
  const path = `${opts.idCompany}/poses_custom/${opts.filename}`;
  await supabase.storage.from(BUCKET).remove([path]); // best-effort
  const { error } = await supabase
    .from('poses_personalizadas')
    .delete()
    .eq('id', opts.id);
  if (error) throw error;
}
